import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Heart, Menu, X, Shield, Globe } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { cartItemCount, wishlist } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/shop', label: t('nav.shop') },
    { to: '/about', label: t('nav.about') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <Shield className="w-8 h-8 text-[#FF0099] group-hover:animate-pulse" />
            <span className="font-heading text-lg sm:text-xl font-bold tracking-tight">
              <span className="text-white">FIGHT LIKE A </span>
              <span className="text-[#FF0099] neon-pink">GYAL</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`text-sm font-bold uppercase tracking-wider transition-colors ${
                  isActive(link.to)
                    ? 'text-[#FF0099] neon-pink'
                    : 'text-white hover:text-[#FF0099]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-[#FF0099] hover:bg-transparent"
                  data-testid="language-selector"
                >
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A1A1A] border-[#333333]">
                <DropdownMenuItem 
                  onClick={() => changeLanguage('en')}
                  className={`cursor-pointer ${i18n.language === 'en' ? 'text-[#FF0099]' : 'text-white'}`}
                  data-testid="lang-en"
                >
                  🇬🇧 English
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('es')}
                  className={`cursor-pointer ${i18n.language === 'es' ? 'text-[#FF0099]' : 'text-white'}`}
                  data-testid="lang-es"
                >
                  🇪🇸 Español
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('fr')}
                  className={`cursor-pointer ${i18n.language === 'fr' ? 'text-[#FF0099]' : 'text-white'}`}
                  data-testid="lang-fr"
                >
                  🇫🇷 Français
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Wishlist */}
            <Link 
              to="/wishlist" 
              className="relative p-2 text-white hover:text-[#FF0099] transition-colors"
              data-testid="nav-wishlist"
            >
              <Heart className="w-5 h-5" />
              {wishlist.items.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF0099] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {wishlist.items.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link 
              to="/cart" 
              className="relative p-2 text-white hover:text-[#FF0099] transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF0099] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-[#FF0099] hover:bg-transparent"
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#050505] border-[#333333] w-64">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-bold uppercase tracking-wider transition-colors ${
                        isActive(link.to)
                          ? 'text-[#FF0099] neon-pink'
                          : 'text-white hover:text-[#FF0099]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
